import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Heart, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const SHOWN_KEY = 'lotterics_donation_shown';
const LEGACY_NEVER_KEY = 'lotterics-donation-popup-never';
const VIEWS_KEY = 'lotterics_popup_views';
const API = process.env.REACT_APP_BACKEND_URL || '';

function trackEvent(event) {
  try {
    fetch(`${API}/api/donation-analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event }),
    }).catch(() => {});
  } catch { /* fire-and-forget */ }
}

export const DonationPopup = () => {
  const [visible, setVisible] = useState(false);
  const { t, currentLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const dialogRef = useRef(null);
  const previousFocus = useRef(null);
  const thresholdRef = useRef(3);

  const isDonate = location.pathname.includes('/donate');

  useEffect(() => {
    if (isDonate) return;

    // Already shown permanently or legacy opt-out
    if (localStorage.getItem(SHOWN_KEY) === '1' || localStorage.getItem(LEGACY_NEVER_KEY) === '1') return;

    // Increment page-view counter
    const views = parseInt(localStorage.getItem(VIEWS_KEY) || '0', 10) + 1;
    localStorage.setItem(VIEWS_KEY, String(views));

    // Fetch threshold then decide
    fetch(`${API}/api/donation-popup/settings`)
      .then(r => r.json())
      .then(data => {
        const threshold = data?.page_view_threshold ?? 3;
        thresholdRef.current = threshold;
        if (views >= threshold) {
          const timer = setTimeout(() => {
            previousFocus.current = document.activeElement;
            setVisible(true);
            trackEvent('shown');
          }, 800);
          return () => clearTimeout(timer);
        }
      })
      .catch(() => {
        // Fallback: use default threshold of 3
        if (views >= 3) {
          const timer = setTimeout(() => {
            previousFocus.current = document.activeElement;
            setVisible(true);
            trackEvent('shown');
          }, 800);
          return () => clearTimeout(timer);
        }
      });
  }, [isDonate]);

  const dismiss = useCallback(() => {
    localStorage.setItem(SHOWN_KEY, '1');
    trackEvent('dismiss');
    setVisible(false);
    if (previousFocus.current) previousFocus.current.focus();
  }, []);

  const handleDonate = useCallback(() => {
    localStorage.setItem(SHOWN_KEY, '1');
    trackEvent('donate_click');
    setVisible(false);
    navigate(`/${currentLang}/donate`);
  }, [currentLang, navigate]);

  // Focus trap + escape
  useEffect(() => {
    if (!visible) return;
    const dialog = dialogRef.current;
    if (dialog) dialog.focus();

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        dismiss();
        return;
      }
      if (e.key === 'Tab' && dialog) {
        const focusable = dialog.querySelectorAll('button, a, [tabindex]:not([tabindex="-1"])');
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [visible, dismiss]);

  if (!visible || isDonate) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      role="presentation"
      onClick={dismiss}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm lotterics-popup-fade" />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('donationPopup.title')}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[480px] bg-[#0a0d14] border border-amber-500/25 rounded-2xl p-6 sm:p-8 shadow-2xl shadow-amber-900/10 lotterics-popup-fade"
        data-testid="donation-popup"
      >
        {/* Close X */}
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 p-1.5 text-gray-500 hover:text-gray-300 rounded-lg hover:bg-white/5 transition-colors"
          aria-label="Close"
          data-testid="donation-popup-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Icon + Title */}
        <div className="flex flex-col items-center text-center mb-5">
          <div className="p-3 bg-gradient-to-br from-amber-500/20 to-yellow-600/20 rounded-full mb-3">
            <Heart className="w-7 h-7 text-amber-500" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">
            {t('donationPopup.title')}
          </h2>
          <p className="text-gray-400 text-sm mt-2 leading-relaxed max-w-sm">
            {t('donationPopup.message')}
          </p>
        </div>

        {/* Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleDonate}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-400 hover:to-yellow-500 text-black font-semibold rounded-xl transition-all duration-200"
            data-testid="donation-popup-donate-btn"
          >
            <Heart className="w-4 h-4" />
            {t('donationPopup.donateButton')}
          </button>

          <button
            onClick={dismiss}
            className="w-full px-5 py-3 text-gray-400 hover:text-white border border-gray-700/50 hover:border-gray-600 rounded-xl font-medium transition-all duration-200"
            data-testid="donation-popup-continue-btn"
          >
            {t('donationPopup.continueButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DonationPopup;
